import type { ProtoWallet, ProtoWalletAccountAPI, ProtoWalletOptions } from '@ducat-unit/client-sdk';

import type { NetworkSettingsConfig } from '@/config/config.types';
import type { WalletConnector } from '@/lib/connector';
import type { MetaMaskProvider } from '@/lib/metamask-provider';
import { getWalletConnector, loadWalletConnector } from '@/lib/util';
import { registerWalletType } from '@/lib/vault-wallet-policy';
import { WalletActionGuard, WalletActionInvalidatedError } from '@/lib/wallet-action-guard';

import type { WalletInfo, WalletType } from './types';
import { createWalletAccountSnapshot, type WalletAccountSnapshot } from './wallet-account';

export function createCurrentWalletActionGuard(params: {
  config: NetworkSettingsConfig | undefined;
  getRevision: () => number;
  revision: number;
  snapshot: WalletAccountSnapshot | null;
}): WalletActionGuard {
  const { config, snapshot } = params;
  if (!snapshot || !config) throw new WalletActionInvalidatedError('intent', 'account-unavailable');
  const connector = getWalletConnector(snapshot.walletType);
  return new WalletActionGuard({
    snapshot,
    revision: params.revision,
    getRevision: params.getRevision,
    prepareForAction: connector.prepareForAction?.bind(connector),
    readCurrentSnapshot: async () => {
      const current = await connector.readCurrentAccount();
      const wallet = await configureProtoWallet({ accounts: current.account, config, walletConnector: connector });
      return {
        snapshot: createWalletAccountSnapshot({ account: wallet.account, network: snapshot.network, walletType: snapshot.walletType }),
        observedNetwork: current.observedNetwork,
      };
    },
  });
}

export async function connectWallet(params: {
  config: NetworkSettingsConfig;
  walletType: WalletType;
  explicitUserAction?: boolean;
  connectedMetaMaskProvider?: MetaMaskProvider;
}): Promise<{
  wallet?: ProtoWallet;
  /** Key inside the `notifications` message namespace, resolved by the caller. */
  errorMessageKey?: string;
}> {
  const { config, walletType, explicitUserAction = false, connectedMetaMaskProvider } = params;

  await loadWalletConnector(walletType);
  const walletConnector = getWalletConnector(walletType);

  if (!walletConnector) {
    throw new Error(`Cant find wallet connector for ${walletType}`);
  }

  try {
    // Fetch accounts from wallet.
    const accounts = await walletConnector.getWalletAccounts({ allowNetworkSwitch: explicitUserAction, connectedMetaMaskProvider });
    return {
      wallet: await configureProtoWallet({ accounts, config, walletConnector }),
    };
  } catch (error) {
    if ((error as Error).message === 'invalid_address_type') {
      return { errorMessageKey: 'errors.wallet.unisatTaprootRequired' };
    }
    throw error;
  }
}

export async function reconnectWallet(params: { walletInfo: WalletInfo; config: NetworkSettingsConfig }): Promise<{
  wallet: ProtoWallet;
  walletType: WalletType;
}> {
  const { walletInfo, config } = params;
  const selectedNetwork = config.network;

  await loadWalletConnector(walletInfo.type);
  const walletConnector = getWalletConnector(walletInfo.type);

  if (!walletConnector) {
    throw new Error(`Cant find wallet connector for ${walletInfo.type}`);
  }

  if (walletInfo.network !== selectedNetwork) {
    throw new Error(`Persisted wallet network is different from app's active network. Please reconnect`);
  }

  const accounts = await walletConnector.reconnect?.(walletInfo);

  if (!accounts) {
    throw new Error('Was not able to retrieve wallet accounts');
  }

  const wallet = await configureProtoWallet({ accounts, config, walletConnector });

  return {
    wallet,
    walletType: walletInfo.type,
  };
}

interface ConfigreProtoWalletParams {
  accounts: ProtoWalletAccountAPI;
  config: NetworkSettingsConfig;
  walletConnector: WalletConnector;
}

export async function configureProtoWallet({ accounts, config, walletConnector }: ConfigreProtoWalletParams): Promise<ProtoWallet> {
  const { ProtoWallet } = await import('@ducat-unit/client-sdk');
  const chain_network = config.network;
  const options: ProtoWalletOptions = {
    asset_postage: 1_000, // The amount of sats to use for coins that carry an asset.
    chain_network,
    txfee_rate: 1, // The default transaction fee rate to pay (in sats/vbyte).
    txfee_reserve: 1_000, // The amount of sats to reserve for covering coin fees.
    // observability: getTracerObservability(),
  };
  // const wallet = new ProtoWallet(accounts, contract, walletConnector.api, WALLET_CFG);
  const proto_wallet = new ProtoWallet(accounts, walletConnector.api, options);

  registerWalletType(proto_wallet, walletConnector.walletType);
  return proto_wallet;
}
